import { useState, useEffect } from 'react';
import {
  Wrench,
  CheckCircle2,
  XCircle,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { ChatMessage } from '@/lib/types';

interface ToolCallGroupProps {
  messages: ChatMessage[];
  onApprove?: (toolCallId: string) => void;
  onReject?: (toolCallId: string, feedback?: string) => void;
}

/** Determine whether a tool is an MCP/connector tool (backend) vs a frontend Office tool */
function isMcpTool(toolName?: string): boolean {
  if (!toolName) return false;
  return toolName.includes('__') || toolName.includes('.');
}

/** Get a human-readable display name for the tool */
function getToolDisplayName(toolName?: string): string {
  if (!toolName) return 'Unknown tool';
  if (toolName.includes('__')) {
    return toolName.split('__').pop() || toolName;
  }
  if (toolName.includes('.')) {
    return toolName.split('.').pop() || toolName;
  }
  return toolName;
}

/** Get tool source label */
function getToolSourceLabel(toolName?: string): string | null {
  if (!toolName) return null;
  if (toolName.includes('__')) return toolName.split('__')[0];
  if (toolName.includes('.')) return toolName.split('.')[0];
  return null;
}

export function ToolCallGroup({ messages, onApprove, onReject }: ToolCallGroupProps) {
  const hasAwaitingApproval = messages.some((m) => m.toolStatus === 'awaiting_approval');
  const [expanded, setExpanded] = useState(hasAwaitingApproval);

  // Auto-expand when awaiting approval
  useEffect(() => {
    if (hasAwaitingApproval) setExpanded(true);
  }, [hasAwaitingApproval]);

  if (messages.length === 0) return null;

  const completedCount = messages.filter((m) => m.toolStatus === 'success').length;
  const errorCount = messages.filter((m) => m.toolStatus === 'error').length;
  const awaitingCount = messages.filter((m) => m.toolStatus === 'awaiting_approval').length;
  const pendingCount = messages.length - completedCount - errorCount - awaitingCount;

  const allComplete = completedCount === messages.length;
  const hasErrors = errorCount > 0;
  const hasAwaiting = awaitingCount > 0;

  // Dynamic summary text
  const parts: string[] = [];
  if (completedCount > 0) parts.push(`${completedCount} complete`);
  if (errorCount > 0) parts.push(`${errorCount} failed`);
  if (awaitingCount > 0) parts.push(`${awaitingCount} awaiting`);
  if (pendingCount > 0) parts.push(`${pendingCount} running`);

  // Border color based on overall state
  const borderClass = hasErrors
    ? 'tool-call-border-error'
    : hasAwaiting
      ? 'tool-call-border'
      : allComplete
        ? 'tool-call-border-success'
        : 'tool-call-border';

  return (
    <div className="animate-fade-in-up min-w-0 overflow-hidden">
      <div className={cn(borderClass, 'min-w-0 overflow-hidden')}>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1.5 w-full text-left group py-0.5"
        >
          {/* Group status icon */}
          {hasErrors ? (
            <XCircle className="size-3.5 text-destructive shrink-0" />
          ) : hasAwaiting ? (
            <AlertCircle className="size-3.5 text-amber-500 shrink-0" />
          ) : allComplete ? (
            <CheckCircle2 className="size-3.5 text-success shrink-0" />
          ) : (
            <Wrench className="size-3.5 text-primary shrink-0 animate-wrench" />
          )}

          <div className="flex-1 min-w-0">
            <span className={cn(
              'text-xs font-medium text-muted-foreground',
              pendingCount > 0 && !allComplete && 'shimmer-text',
            )}>
              {messages.length} tool{messages.length > 1 ? 's' : ''}
            </span>
            <span className="text-[10px] text-muted-foreground/60 ml-1.5">
              {parts.join(' · ')}
            </span>
          </div>

          {expanded ? (
            <ChevronDown className="size-3 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="size-3 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </button>

        {expanded && (
          <div className="mt-1 space-y-0.5 tool-collapsible-enter">
            {messages.map((msg) => (
              <ToolCallItem
                key={msg.id}
                message={msg}
                onApprove={onApprove}
                onReject={onReject}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/** Individual tool call item within a group — collapsible with log details */
function ToolCallItem({ message, onApprove, onReject }: {
  message: ChatMessage;
  onApprove?: (toolCallId: string) => void;
  onReject?: (toolCallId: string, feedback?: string) => void;
}) {
  const hasDetails = !!(message.toolArgs || message.toolResult != null || message.toolError);
  // Closed by default - user can open manually if they want to see details
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const displayName = getToolDisplayName(message.toolName);
  const sourceLabel = getToolSourceLabel(message.toolName);
  const isMcp = isMcpTool(message.toolName);
  const isPending = message.toolStatus === 'pending';
  const isComplete = message.toolStatus === 'success';
  const isError = message.toolStatus === 'error';
  const isAwaiting = message.toolStatus === 'awaiting_approval';

  // State-based title
  const title = isAwaiting
    ? `Requesting ${displayName}`
    : isPending
      ? isMcp ? 'Using MCP tool' : `Using ${displayName}`
      : isComplete
        ? isMcp ? 'Triggered MCP tool' : `Triggered ${displayName}`
        : isError
          ? `${displayName} failed`
          : displayName;

  return (
    <div className="tool-call-border-muted min-w-0 overflow-hidden">
      <button
        type="button"
        onClick={() => hasDetails && setDetailsOpen(!detailsOpen)}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        className="flex items-center gap-1.5 text-left w-full group py-0.5 min-w-0"
      >
        <ToolStatusIcon status={message.toolStatus} />

        <span className={cn(
          'text-[11px] font-medium text-muted-foreground truncate min-w-0 flex-1',
          isPending && 'shimmer-text',
        )}>
          {title}
        </span>

        {isAwaiting && (
          <span className="text-[9px] text-amber-600 bg-amber-500/10 px-1 rounded shrink-0">
            approval
          </span>
        )}

        {isMcp && sourceLabel && (
          <span className="text-[9px] text-muted-foreground bg-muted px-1 rounded shrink-0 flex items-center gap-0.5">
            <ExternalLink className="size-2" />
            {sourceLabel}
          </span>
        )}

        {hasDetails && detailsOpen ? (
          <ChevronDown className="size-2.5 text-muted-foreground shrink-0" />
        ) : hasDetails && isHovering ? (
          <ChevronRight className="size-2.5 text-muted-foreground shrink-0" />
        ) : null}
      </button>

      {/* Expandable details */}
      {detailsOpen && hasDetails && (
        <div className="mt-0.5 ml-5 space-y-1 tool-collapsible-enter min-w-0 overflow-hidden">
          {/* Input params — key:value pairs */}
          {message.toolArgs && Object.keys(message.toolArgs).length > 0 && (
            <div className="max-h-28 overflow-y-auto overflow-x-hidden custom-scrollbar font-mono text-[10px] break-all">
              {isMcp && (
                <div className="mb-0.5">
                  <span className="text-muted-foreground">toolName:</span>{' '}
                  <span className="text-foreground/80">{displayName}</span>
                </div>
              )}
              {Object.entries(message.toolArgs).map(([key, value]) => (
                <div key={key} className="mb-0.5">
                  <span className="text-muted-foreground">{key}:</span>{' '}
                  <span className="text-foreground/80 break-all">{formatArgValue(value)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {message.toolError && (
            <div className="text-[10px] font-mono text-destructive/80 bg-destructive/5 rounded px-1.5 py-0.5 border border-destructive/10 overflow-hidden">
              <pre className="whitespace-pre-wrap break-all">{message.toolError}</pre>
            </div>
          )}

          {/* Output */}
          {message.toolResult != null && (
            <div className="max-h-28 overflow-y-auto overflow-x-hidden custom-scrollbar">
              <pre className="whitespace-pre-wrap break-all font-mono text-[10px] text-foreground/70">{
                typeof message.toolResult === 'string'
                  ? truncateOutput(message.toolResult)
                  : truncateOutput(JSON.stringify(message.toolResult, null, 2))
              }</pre>
            </div>
          )}

          {/* Processing state */}
          {isPending && !message.toolResult && !message.toolError && (
            <div className="text-[10px] text-muted-foreground shimmer-text">
              Processing...
            </div>
          )}
        </div>
      )}

      {/* Approval buttons */}
      {isAwaiting && message.toolCallId && (
        <div className="flex items-center gap-2 mt-0.5 ml-5">
          <Button
            size="sm"
            variant="default"
            className="h-5 text-[10px] px-2"
            onClick={() => onApprove?.(message.toolCallId!)}
          >
            <CheckCircle2 className="size-2.5 mr-0.5" />
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-5 text-[10px] px-2"
            onClick={() => onReject?.(message.toolCallId!)}
          >
            <XCircle className="size-2.5 mr-0.5" />
            Reject
          </Button>
        </div>
      )}
    </div>
  );
}

/** Status icon for a tool call — matches HAI pattern with animated wrench */
function ToolStatusIcon({ status }: { status?: ChatMessage['toolStatus'] }) {
  switch (status) {
    case 'awaiting_approval':
      return <AlertCircle className="size-3 text-amber-500 shrink-0" />;
    case 'success':
      return <CheckCircle2 className="size-3 text-success shrink-0" />;
    case 'error':
      return <XCircle className="size-3 text-destructive shrink-0" />;
    case 'pending':
    default:
      return <Wrench className="size-3 text-primary shrink-0 animate-wrench" />;
  }
}

/** Format a tool argument value for display */
function formatArgValue(value: unknown): string {
  if (typeof value === 'string') {
    return value.length > 150 ? value.slice(0, 150) + '...' : value;
  }
  const str = JSON.stringify(value);
  return str.length > 150 ? str.slice(0, 150) + '...' : str;
}

/** Truncate long output for display */
function truncateOutput(output: string, maxLen = 400): string {
  if (output.length <= maxLen) return output;
  return output.slice(0, maxLen) + `\n... (${output.length - maxLen} more chars)`;
}
